import { Button, ButtonProps, SxProps, Theme, Typography } from '@mui/material';
import React, { FC, useCallback } from 'react';
import useConfirmationDialog, {
  DialogButtonColors,
  DialogButtonVariants,
} from 'src/components/ConfirmationDialog/view';
import { useEditContactDrawerController } from '../../controllers/EditContactDrawerController';

type Props = {
  selectedCompanyId: string;
  contactId: string;
  nextAction?: () => void;
};

const useMarkContactAsOptOutDialog = () => {
  const { openDialog, ConfirmationDialog } = useConfirmationDialog();
  const editContactDrawerController = useEditContactDrawerController();

  const handleMarkContactAsOptOut = useCallback(
    ({ selectedCompanyId, contactId, nextAction }: Props) => {
      const dialogConfig = {
        title: `Opt-out Contact`,
        content: `By opting-out this contact, you will no longer be able to send messages to this user.`,
        confirmButtonText: 'Opt-out',
        confirmButtonColor: 'error' as DialogButtonColors,
        cancelButtonColor: 'primary' as DialogButtonColors,
        cancelButtonVariant: 'contained' as DialogButtonVariants,
      };

      openDialog({
        ...dialogConfig,
        onConfirm: () => {
          editContactDrawerController
            .handleOptOutContact(contactId, selectedCompanyId!)
            .then(() => {
              nextAction?.();
            });
        },
      });
    },
    [],
  );

  const MarkContactAsOptOutButton: FC<Props & ButtonProps> = useCallback(
    props => (
      <Button
        fullWidth={props.fullWidth}
        variant="outlined"
        sx={validButtonSx}
        onClick={() => handleMarkContactAsOptOut(props)}
      >
        <Typography variant="body2" sx={validButtonTitle}>Opt-out Contact</Typography>
      </Button>
    ),
    [handleMarkContactAsOptOut],
  );

  return {
    handleMarkContactAsOptOut,
    MarkContactAsOptOutButton,
    MarkContactAsOptOutDialog: ConfirmationDialog,
  };
};

const validButtonSx: SxProps<Theme> = {
  padding: 0,
  marginRight: '10px',
  minWidth: '110px',
  color: 'grey.600',
  borderColor: 'grey.600',
  '&:hover': {
    borderColor: 'grey.600',
  },
};

const validButtonTitle: SxProps<Theme> = {
  padding: '5px',
};

export default useMarkContactAsOptOutDialog;
